import {
  getARRecoveryNotesByAccount,
  getActivityByRecord,
  getAuditByRecord,
  getCollectionLetterHistoryByAccount,
  getTicklersByRecord,
  type UniversalActivityEvent,
  type UniversalAuditTrailEntry,
  type UniversalOperationalFoundationState,
  type UniversalOperationalRecord,
  type UniversalTickler,
  type UniversalWorkspaceKey,
} from "./universalOperationalEngines";

export type RegistryReadinessLevel = "Healthy" | "Warning" | "Critical";

export interface RegistryRecordStats {
  notes: number;
  businessNotes: number;
  arNotes: number;
  ticklers: UniversalTickler[];
  activity: UniversalActivityEvent[];
  audit: UniversalAuditTrailEntry[];
  letterHistory: number;
  openTicklers: number;
  completedTicklers: number;
  escalatedTicklers: number;
  overdueTicklers: number;
}

export interface RegistryReadiness {
  score: number;
  level: RegistryReadinessLevel;
  missing: string[];
}

export interface RegistryHealth {
  totalRecords: number;
  healthyRecords: number;
  warningRecords: number;
  criticalRecords: number;
  recordsMissingOwners: number;
  recordsMissingActivity: number;
  recordsMissingNotes: number;
  recordsMissingTicklers: number;
  recordsMissingAuditHistory: number;
  brokenLinks: number;
}

export interface WorkspaceHealth {
  workspace: UniversalWorkspaceKey;
  registeredRecords: number;
  notesCount: number;
  ticklerCount: number;
  activityCount: number;
  auditCount: number;
  healthyRecords: number;
  warningRecords: number;
  criticalRecords: number;
}

export interface RegistryDiagnosticsSnapshot {
  generatedAt: string;
  registryHealth: RegistryHealth;
  workspaceHealth: WorkspaceHealth[];
  missingOwners: UniversalOperationalRecord[];
  missingNotes: UniversalOperationalRecord[];
  missingTicklers: UniversalOperationalRecord[];
  missingActivity: UniversalOperationalRecord[];
  missingAudit: UniversalOperationalRecord[];
  brokenLinks: UniversalOperationalRecord[];
}

export const registryWorkspaceOrder: UniversalWorkspaceKey[] = [
  "business-center-os",
  "ar-recovery-os",
  "truevance-connect-os",
  "kreatethedate-os",
  "memory-media-vault-os",
  "future-template",
];

export function getActiveRegistryRecords(state: UniversalOperationalFoundationState) {
  return state.operationalRecords.filter((record) => !record.archivedAt);
}

export function getRegistryRecordStats(state: UniversalOperationalFoundationState, record: UniversalOperationalRecord): RegistryRecordStats {
  const businessNotes = state.businessNotes.filter(
    (note) => note.workspace === record.workspace && note.recordType === record.recordType && note.recordId === record.id && note.status !== "archived",
  ).length;
  const arNotes = record.workspace === "ar-recovery-os" && record.recordType === "ar_account" ? getARRecoveryNotesByAccount(state, record.id).length : 0;
  const ticklers = getTicklersByRecord(state, record.workspace, record.recordType, record.id);
  const activity = getActivityByRecord(state, record.workspace, record.recordType, record.id);
  const audit = getAuditByRecord(state, record.workspace, record.recordType, record.id);
  const letterHistory = record.workspace === "ar-recovery-os" && record.recordType === "ar_account" ? getCollectionLetterHistoryByAccount(state, record.id).length : 0;
  const today = new Date().toISOString().slice(0, 10);

  return {
    notes: businessNotes + arNotes,
    businessNotes,
    arNotes,
    ticklers,
    activity,
    audit,
    letterHistory,
    openTicklers: ticklers.filter((tickler) => tickler.status === "Open").length,
    completedTicklers: ticklers.filter((tickler) => tickler.status === "Completed").length,
    escalatedTicklers: ticklers.filter((tickler) => tickler.status === "Escalated").length,
    overdueTicklers: ticklers.filter((tickler) => tickler.status === "Overdue" || tickler.dueDate < today).length,
  };
}

export function getRegistryStatsMap(state: UniversalOperationalFoundationState, records = getActiveRegistryRecords(state)) {
  const map = new Map<string, RegistryRecordStats>();
  records.forEach((record) => {
    map.set(record.id, getRegistryRecordStats(state, record));
  });
  return map;
}

export function emptyRegistryRecordStats(): RegistryRecordStats {
  return {
    notes: 0,
    businessNotes: 0,
    arNotes: 0,
    ticklers: [],
    activity: [],
    audit: [],
    letterHistory: 0,
    openTicklers: 0,
    completedTicklers: 0,
    escalatedTicklers: 0,
    overdueTicklers: 0,
  };
}

export function getRecordReadiness(record: UniversalOperationalRecord, stats: RegistryRecordStats): RegistryReadiness {
  const missing = [
    !record.owner ? "Owner" : "",
    stats.notes === 0 ? "Notes" : "",
    stats.activity.length === 0 ? "Activity" : "",
    stats.audit.length === 0 ? "Audit" : "",
  ].filter(Boolean);
  const score = Math.max(0, 100 - missing.length * 25);
  const level: RegistryReadinessLevel = score >= 100 ? "Healthy" : score >= 75 ? "Warning" : "Critical";
  return { score, level, missing };
}

export function getMissingOwners(records: UniversalOperationalRecord[]) {
  return records.filter((record) => !record.owner);
}

export function getMissingNotes(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>) {
  return records.filter((record) => (statsByRecordId.get(record.id) || emptyRegistryRecordStats()).notes === 0);
}

export function getMissingTicklers(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>) {
  return records.filter((record) => (statsByRecordId.get(record.id) || emptyRegistryRecordStats()).ticklers.length === 0);
}

export function getMissingActivity(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>) {
  return records.filter((record) => (statsByRecordId.get(record.id) || emptyRegistryRecordStats()).activity.length === 0);
}

export function getMissingAudit(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>) {
  return records.filter((record) => (statsByRecordId.get(record.id) || emptyRegistryRecordStats()).audit.length === 0);
}

export function getBrokenLinks(records: UniversalOperationalRecord[]) {
  const recordIds = new Set(records.map((record) => record.id));
  return records.filter(
    (record) => (record.relatedClientId && !recordIds.has(record.relatedClientId)) || (record.relatedProjectId && !recordIds.has(record.relatedProjectId)),
  );
}

export function getRegistryHealth(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>): RegistryHealth {
  const readiness = records.map((record) => getRecordReadiness(record, statsByRecordId.get(record.id) || emptyRegistryRecordStats()));
  return {
    totalRecords: records.length,
    healthyRecords: readiness.filter((item) => item.level === "Healthy").length,
    warningRecords: readiness.filter((item) => item.level === "Warning").length,
    criticalRecords: readiness.filter((item) => item.level === "Critical").length,
    recordsMissingOwners: getMissingOwners(records).length,
    recordsMissingActivity: getMissingActivity(records, statsByRecordId).length,
    recordsMissingNotes: getMissingNotes(records, statsByRecordId).length,
    recordsMissingTicklers: getMissingTicklers(records, statsByRecordId).length,
    recordsMissingAuditHistory: getMissingAudit(records, statsByRecordId).length,
    brokenLinks: getBrokenLinks(records).length,
  };
}

export function getWorkspaceHealth(
  state: UniversalOperationalFoundationState,
  records: UniversalOperationalRecord[],
  statsByRecordId: Map<string, RegistryRecordStats>,
): WorkspaceHealth[] {
  return registryWorkspaceOrder.map((workspace) => {
    const workspaceRecords = records.filter((record) => record.workspace === workspace);
    const readiness = workspaceRecords.map((record) => getRecordReadiness(record, statsByRecordId.get(record.id) || emptyRegistryRecordStats()));
    return {
      workspace,
      registeredRecords: workspaceRecords.length,
      notesCount:
        state.businessNotes.filter((note) => note.workspace === workspace && note.status !== "archived").length +
        (workspace === "ar-recovery-os" ? state.arRecoveryNotes.length : 0),
      ticklerCount: state.ticklers.filter((tickler) => tickler.workspace === workspace).length,
      activityCount: state.activityEvents.filter((event) => event.workspace === workspace).length,
      auditCount: state.auditTrail.filter((entry) => entry.workspace === workspace).length,
      healthyRecords: readiness.filter((item) => item.level === "Healthy").length,
      warningRecords: readiness.filter((item) => item.level === "Warning").length,
      criticalRecords: readiness.filter((item) => item.level === "Critical").length,
    };
  });
}

export function getRegistryDiagnosticsSnapshot(
  state: UniversalOperationalFoundationState,
  records = getActiveRegistryRecords(state),
): RegistryDiagnosticsSnapshot {
  const statsByRecordId = getRegistryStatsMap(state, records);
  return {
    generatedAt: new Date().toISOString(),
    registryHealth: getRegistryHealth(records, statsByRecordId),
    workspaceHealth: getWorkspaceHealth(state, records, statsByRecordId),
    missingOwners: getMissingOwners(records),
    missingNotes: getMissingNotes(records, statsByRecordId),
    missingTicklers: getMissingTicklers(records, statsByRecordId),
    missingActivity: getMissingActivity(records, statsByRecordId),
    missingAudit: getMissingAudit(records, statsByRecordId),
    brokenLinks: getBrokenLinks(records),
  };
}

export function buildRegistryExportRows(records: UniversalOperationalRecord[], statsByRecordId: Map<string, RegistryRecordStats>) {
  return records.map((record) => {
    const stats = statsByRecordId.get(record.id) || emptyRegistryRecordStats();
    const readiness = getRecordReadiness(record, stats);
    return {
      id: record.id,
      recordName: record.recordName,
      workspace: record.workspace,
      recordType: record.recordType,
      owner: record.owner || "",
      status: record.status || "",
      source: record.source || "",
      route: record.route || "",
      relatedClientId: record.relatedClientId || "",
      relatedProjectId: record.relatedProjectId || "",
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      notes: stats.notes,
      businessNotes: stats.businessNotes,
      arNotes: stats.arNotes,
      ticklers: stats.ticklers.length,
      openTicklers: stats.openTicklers,
      completedTicklers: stats.completedTicklers,
      escalatedTicklers: stats.escalatedTicklers,
      overdueTicklers: stats.overdueTicklers,
      activity: stats.activity.length,
      audit: stats.audit.length,
      letterHistory: stats.letterHistory,
      readinessScore: readiness.score,
      readinessLevel: readiness.level,
      readinessMissing: readiness.missing.join("; "),
    };
  });
}
